import { useState } from "react";
import { ChevronRight } from "lucide-react";

/**
 * CollapsibleGroup — a reusable accordion section for long lists.
 * Header shows a title, an optional icon/subtitle and a count badge; the body
 * (children) is hidden until expanded. Starts collapsed by default so a long
 * page stays scannable. Use `forceOpen` to drive open state externally (e.g.
 * to auto-expand groups that match a search query).
 */
export const CollapsibleGroup = ({
  title,
  count,
  subtitle,
  defaultOpen = false,
  forceOpen,
  icon: Icon,
  testid,
  accent = false,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = forceOpen !== undefined ? forceOpen : internalOpen;

  return (
    <div className="overflow-hidden rounded-xl border border-black/5 bg-white" data-testid={testid}>
      <button
        type="button"
        onClick={() => setInternalOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-ek-purple/[0.03]"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <ChevronRight size={16} className={`shrink-0 text-ek-ink/65 transition-transform ${open ? "rotate-90" : ""}`} />
          {Icon && <Icon size={15} className="shrink-0 text-ek-purple" />}
          <span className="truncate text-sm font-semibold text-ek-ink">{title}</span>
          {subtitle && <span className="hidden truncate text-xs text-ek-ink/65 sm:inline">· {subtitle}</span>}
        </div>
        <span
          data-testid={testid ? `${testid}-count` : undefined}
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${accent ? "bg-ek-purple/10 text-ek-purple" : "bg-ek-canvas text-ek-ink/65"}`}
        >
          {count}
        </span>
      </button>
      {open && (
        <div className="border-t border-black/5 px-4 py-1" data-testid={testid ? `${testid}-body` : undefined}>
          {children}
        </div>
      )}
    </div>
  );
};
